import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { Link } from 'react-router-dom';
import {
  Alert, Button, CircularProgress, FormControl, InputLabel, MenuItem,
  Select, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import Box from 'elements/Box';
import { ProjectConfig } from 'Global';

type BiographyStatus = 'cargada' | 'breve' | 'sin_biografia' | 'por_revisar';
type StatusFilter = 'todos' | BiographyStatus;

interface AuditRow {
  aniversario_id: number;
  name: string;
  status: BiographyStatus;
  bioHTML_visible_chars: number;
  bioJSON_present: boolean;
  historical_snapshot_candidate?: boolean;
  completed_empty_source_jobs?: number;
  spanish_lang1_visible_chars?: number;
  spanish_lang1_json_present?: boolean;
  languages?: Record<string, {status: string; characters: number; same_as_spanish: boolean}>;
}
const languageCodes = ['es', 'en', 'it', 'pt', 'fr', 'ca', 'la'];
const languageTitles: Record<string,string> = {
  es:'Español',en:'Inglés',it:'Italiano',pt:'Portugués',
  fr:'Francés',ca:'Catalán',la:'Latín',
};
const languageStatus: Record<string,string> = {
  cargada:'Con texto',breve:'Breve',sin_biografia:'Vacía',
  sin_texto:'Vacía',por_revisar:'Provisional',
};
type ImportRow = {id:number; name:string; source_name:string; status:string; source_words:number; warning:string[]};
type ImportPreview = {success:boolean; pack_sha256:string; source_document:string; ready:number; rows:ImportRow[]};
const importStatuses: Record<string,string> = {
  lista_para_cargar:'Lista para incorporar',ya_cargada:'Ya contiene biografía',
  json_preexistente:'Existe contenido estructurado',revisar_fuente:'Revisar la fuente antes de publicar',
  identidad_distinta:'El nombre de la ficha cambió',no_existe:'Ficha no encontrada',
};
const descriptions: Record<BiographyStatus, string> = {
  cargada: 'Biografía cargada',
  breve: 'Texto breve; revisar',
  sin_biografia: 'Sin biografía visible',
  por_revisar: 'Texto provisional',
};

function normalized(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export default function AniversariosBiographyAudit() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('todos');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkedAt, setCheckedAt] = useState('');
  const [languageStats, setLanguageStats] = useState<Record<string,Record<string,number>>>({});
  const [historicalQueueChecked, setHistoricalQueueChecked] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<ImportPreview|null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState('');
  const [importMessage, setImportMessage] = useState('');

  const refresh = () => {
    setLoading(true);
    setError('');
    axios.get(ProjectConfig.api_url, {
      params: { request: 'aniversarios_biography_audit' },
      headers: { Authorization: `Bearer ${new Cookies().get('ltkn') || ''}` },
      timeout: 30000,
    }).then(response => {
      const data = response.data;
      if (data?.success !== true || !Array.isArray(data?.list)) {
        throw new Error(data?.message || 'La API no devolvió una auditoría válida.');
      }
      setRows(data.list);
      setLanguageStats(data.language_stats || {});
      setHistoricalQueueChecked(data.historical_queue_checked === true);
      setCheckedAt(data.checked_at || '');
    }).catch(err => {
      setRows([]);
      setCheckedAt('');
      setError(err?.response?.data?.message || err?.message ||
        'No fue posible consultar los datos. Comprueba tu sesión administrativa.');
    }).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => rows.filter(row => (
    (status === 'todos' || row.status === status) &&
    (normalized(row.name).includes(normalized(search)) ||
      String(row.aniversario_id).includes(search.trim()))
  )), [rows, search, status]);

  const counts = useMemo(() => {
    const totals: Record<BiographyStatus, number> = {
      cargada: 0, breve: 0, sin_biografia: 0, por_revisar: 0,
    };
    rows.forEach(row => { totals[row.status] += 1; });
    return totals;
  }, [rows]);

  const importRequest = async (request: string, apply = false): Promise<any> => {
    if (!importFile) throw new Error('Selecciona primero el archivo JSON de las biografías recibidas.');
    const form = new FormData();
    form.append('source_package', importFile, importFile.name);
    if (apply && importPreview) {
      form.append('confirmed_pack_sha256', importPreview.pack_sha256);
      form.append('confirmed_action', 'INCORPORAR_SOLO_BIOGRAFIAS_VACIAS');
    }
    const response = await axios.post(ProjectConfig.api_url, form, {
      params: { request },
      headers: { Authorization: `Bearer ${new Cookies().get('ltkn') || ''}` },
      timeout: 45000,
    });
    if (response.data?.success !== true) {
      throw new Error(response.data?.message || 'La operación fue rechazada por el servidor.');
    }
    return response.data;
  };

  const previewImport = async () => {
    setImportBusy(true); setImportError(''); setImportMessage(''); setImportPreview(null);
    try {
      const response = await importRequest('preview_aniversarios_biography_import');
      setImportPreview(response);
    } catch (err: any) {
      setImportError(err?.response?.data?.message || err?.message || 'No fue posible verificar el paquete.');
    } finally { setImportBusy(false); }
  };

  const applyImport = async () => {
    if (!importPreview || !importPreview.ready) return;
    const approved = window.confirm(
      '¿Incorporar ' + importPreview.ready + ' biografías originales a fichas VACÍAS en español?\\n\\n' +
      'Los registros con conflictos se omitirán. No se sobrescribirán biografías, nombres, fechas ni idiomas.\\n\\n' +
      'El sistema validará otra vez los registros antes de guardar.'
    );
    if (!approved) return;
    setImportBusy(true); setImportError('');
    try {
      const result = await importRequest('apply_aniversarios_biography_import', true);
      setImportMessage('Incorporadas ' + result.writes + ' biografías. IDs: ' +
        (result.updated_ids || []).join(', ') + '. Operación verificada en base de datos.');
      setImportPreview(null);
      refresh();
    } catch (err: any) {
      setImportError(err?.response?.data?.message || err?.message || 'No se pudo aplicar la carga.');
    } finally { setImportBusy(false); }
  };

  const downloadCsv = () => {
    if (!rows.length || loading || error) return;
    const safe = (value: string | number | boolean) => {
      let text = String(value);
      // Proteger hojas de cálculo contra valores interpretados como fórmulas.
      if (/^[=+@-]/.test(text)) text = "'" + text;
      return '"' + text.replace(/"/g, '""') + '"';
    };
    const header = ['ID', 'Sacerdote', 'Estado biografía española', 'Caracteres visibles', 'JSON presente', 'Fecha de auditoría',
      ...languageCodes.map(code => code.toUpperCase() + ' estado'),
      ...languageCodes.map(code => code.toUpperCase() + ' caracteres'),
      ...languageCodes.map(code => code.toUpperCase() + ' igual al español'),
      'Copia histórica posible en cola', 'Tareas completadas desde fuente vacía',
      'ES alternativo (lang_id=1) caracteres', 'ES alternativo JSON presente'];
    const records = filtered.map(row => [
      row.aniversario_id, row.name, descriptions[row.status],
      row.bioHTML_visible_chars, row.bioJSON_present ? 'Sí' : 'No', checkedAt,
      ...languageCodes.map(code => languageStatus[row.languages?.[code]?.status || 'sin_texto'] || 'No disponible'),
      ...languageCodes.map(code => row.languages?.[code]?.characters || 0),
      ...languageCodes.map(code => row.languages?.[code]?.same_as_spanish ? 'Sí' : 'No'),
      row.historical_snapshot_candidate ? 'Sí; validar origen' : 'No',
      row.completed_empty_source_jobs || 0,
      row.spanish_lang1_visible_chars || 0,
      row.spanish_lang1_json_present ? 'Sí' : 'No',
    ]);
    const csv = '\uFEFF' + [header, ...records].map(record =>
      record.map(value => safe(value)).join(';')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'desideria-auditoria-biografias-produccion.csv';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div id="ProductList">
      <div className="buttons_top">
        <Button component={Link} to="/admin/aniversarios">Volver a Aniversarios</Button>
        <Button variant="outlined" onClick={refresh} disabled={loading}>
          {loading ? 'Consultando base de datos…' : 'Actualizar auditoría'}
        </Button>
        <Button variant="contained" onClick={downloadCsv}
          disabled={loading || !rows.length || Boolean(error)}>
          Descargar CSV ({filtered.length})
        </Button>
      </div>
      <Box id="aniversarios_biografias_auditoria" title="Auditoría de biografías" subtitle="Datos reales de Desideria · Solo lectura">
        <Alert severity="info" sx={{ mb: 2 }}>
          Esta consulta comprueba el campo de biografía española (bioHTML) guardado
          en las fichas de producción. «Sin biografía visible» no significa que el rector
          no la haya entregado. No se modifica ningún registro.
        </Alert>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && <CircularProgress />}
        {!loading && !error && <>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{rows.length}</strong> fichas verificadas en esta consulta.{' '}
            <strong>{counts.cargada}</strong> con biografía,{' '}
            <strong>{counts.breve}</strong> con texto breve,{' '}
            <strong>{counts.sin_biografia}</strong> sin biografía visible y{' '}
            <strong>{counts.por_revisar}</strong> con texto provisional.
            {checkedAt && ' Consultado: ' + new Date(checkedAt).toLocaleString('es-CO')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {languageCodes.map(code =>
              <span key={code} style={{display:'inline-block', marginRight:18}}>
                <strong>{code.toUpperCase()}</strong>: {languageStats[code]?.con_texto ?? '?'} con texto,{' '}
                {languageStats[code]?.sin_texto ?? '?'} vacíos
                {(languageStats[code]?.igual_espanol || 0) > 0 &&
                  ' · ' + languageStats[code].igual_espanol + ' iguales al español'}
              </span>)}
          </Typography>
          <Alert severity="warning" sx={{mb:2}}>
            «Con texto» verifica presencia, no calidad ni autenticidad de una traducción.
            La marca =ES señala que el texto coincide íntegramente con el español.
          </Alert>
          {rows.some(row => row.bioHTML_visible_chars === 0 &&
            ((row.spanish_lang1_visible_chars || 0) > 0 || row.spanish_lang1_json_present)) &&
            <Alert severity="warning" sx={{mb:2}}>
              Hay biografías sin texto en el campo principal pero con contenido
              en el registro alternativo español (lang_id=1). No las solicites
              de nuevo ni las sobrescribas: revisa su ID en el CSV.
            </Alert>}
          {historicalQueueChecked && <Alert severity="info" sx={{mb:2}}>
            Se consultó la cola histórica de traducciones. «Copia histórica posible» solo
            identifica una fuente guardada anteriormente: no garantiza autenticidad ni
            permite restaurarla sin revisión. Las tareas completadas sin fuente española
            no certifican biografías traducidas. Exporta el CSV para revisar los IDs.
          </Alert>}
          {!historicalQueueChecked && <Alert severity="info" sx={{mb:2}}>
            No se pudo consultar una cola histórica compatible en esta base de datos.
          </Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField label="Buscar sacerdote o ID" fullWidth size="small"
              value={search} onChange={event => setSearch(event.target.value)} />
            <FormControl size="small" sx={{ minWidth: 230 }}>
              <InputLabel id="aniversarios-audit-status">Estado</InputLabel>
              <Select labelId="aniversarios-audit-status" label="Estado"
                value={status} onChange={event => setStatus(event.target.value as StatusFilter)}>
                <MenuItem value="todos">Todos los estados</MenuItem>
                <MenuItem value="cargada">Biografía cargada</MenuItem>
                <MenuItem value="breve">Texto breve</MenuItem>
                <MenuItem value="sin_biografia">Sin biografía visible</MenuItem>
                <MenuItem value="por_revisar">Texto provisional</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Sacerdote</TableCell>
                {languageCodes.map(code => <TableCell key={code} title={languageTitles[code]}>
                  {code.toUpperCase()}
                </TableCell>)}
                <TableCell>Historial</TableCell>
                <TableCell>Ficha</TableCell>
              </TableRow></TableHead>
              <TableBody>{filtered.map(row => <TableRow key={row.aniversario_id}>
                <TableCell>{row.aniversario_id}</TableCell>
                <TableCell>{row.name}</TableCell>
                {languageCodes.map(code => {
                  const entry = row.languages?.[code];
                  return <TableCell key={code}
                    title={languageTitles[code] + ': ' + (entry?.characters ?? 0) + ' caracteres visibles'}>
                    {languageStatus[entry?.status || 'sin_texto'] || 'No disponible'}
                    {entry?.same_as_spanish && <strong> · =ES</strong>}
                  </TableCell>;
                })}
                <TableCell>
                  {(row.spanish_lang1_visible_chars || 0) > 0
                    ? 'ES alternativo: ' + row.spanish_lang1_visible_chars + ' caracteres'
                    : row.spanish_lang1_json_present
                      ? 'ES alternativo: contenido JSON'
                      : row.historical_snapshot_candidate
                        ? 'Posible copia española anterior'
                        : (row.completed_empty_source_jobs
                          ? row.completed_empty_source_jobs + ' tareas sin texto'
                          : 'Sin copia identificada')}
                </TableCell>
                <TableCell><Button component={Link} size="small"
                  to={`/admin/aniversarios/edit/${row.aniversario_id}`}>Ver</Button></TableCell>
              </TableRow>)}</TableBody>
            </Table>
          </TableContainer>
          {filtered.length === 0 && <Typography sx={{ p: 2 }}>No hay coincidencias.</Typography>}
        </>}
      </Box>
      <Box title="Recuperar biografías recibidas" subtitle="Solo para fichas sin contenido · importación protegida">
        <Alert severity="info" sx={{mb:2}}>
          Selecciona el paquete original verificado de octubre de 2026, incluido «Desideria_Importacion_Segura_42_Biografias_20261017.json».
          El servidor compara su SHA256 con el paquete original, impide sobrescrituras y bloquea
          entradas con cronología o contenido previo que necesiten revisión.
        </Alert>
        <Stack direction={{xs:'column',sm:'row'}} spacing={2} sx={{mb:2}}>
          <input aria-label="Seleccionar paquete de biografías"
            type="file" accept=".json,application/json"
            onChange={event => {
              setImportFile(event.target.files?.[0] || null);
              setImportPreview(null); setImportError(''); setImportMessage('');
            }} />
          <Button variant="outlined" disabled={!importFile || importBusy} onClick={previewImport}>
            {importBusy ? 'Validando…' : 'Revisar paquete antes de cargar'}
          </Button>
          <Button variant="contained" color="primary"
            disabled={importBusy || !importPreview?.ready} onClick={applyImport}>
            Incorporar {importPreview?.ready || 0} biografías seguras
          </Button>
        </Stack>
        {importError && <Alert severity="error" sx={{mb:2}}>{importError}</Alert>}
        {importMessage && <Alert severity="success" sx={{mb:2}}>{importMessage}</Alert>}
        {importPreview && <>
          <Typography sx={{mb:2}}>
            Fuente: {importPreview.source_document}. Aptas para cargar: <strong>{importPreview.ready}</strong>.
            Se omiten y conservan las demás entradas. No afecta otros idiomas.
          </Typography>
          <TableContainer><Table size="small">
            <TableHead><TableRow><TableCell>ID</TableCell><TableCell>Sacerdote</TableCell>
              <TableCell>Estado antes de importar</TableCell><TableCell>Texto original (palabras)</TableCell>
            </TableRow></TableHead>
            <TableBody>{importPreview.rows.map(row => <TableRow key={row.id}>
              <TableCell>{row.id}</TableCell><TableCell>{row.name}</TableCell>
              <TableCell>{importStatuses[row.status] || row.status}</TableCell>
              <TableCell>{row.source_words}</TableCell>
            </TableRow>)}</TableBody>
          </Table></TableContainer>
        </>}
      </Box>
    </div>
  );
}
